import React, { useState, useEffect } from 'react';
import tokenManager from '../utils/tokenManager';
import { X, Plus, Trash2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import 'react-datepicker/dist/react-datepicker.css';

// Preserve the selected calendar date instead of shifting local midnight through UTC.
const serializeLocalDate = (date) => {
  if (!(date instanceof Date)) return date;
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 19);
};

const EventModal = ({ event, onSave, onClose }) => {
  const defaultEventValues = {
    supplier: 'Loveshack ANS',
    supplier_id: 1,
    responsible_technician: 'Sander Eriksen'
  };
  const [formData, setFormData] = useState({
    name: '',
    date: new Date(),
    additional_dates: [],  // Array of additional Date objects for multi-day events
    location: '',
    venue_address: '',
    hotel_name: '',
    hotel_address: '',
    get_in_time: '',
    soundcheck_time: '',
    logistics_required: true,
    venue_width: '',
    venue_height: '',
    venue_depth: '',
    description: '',
    ...defaultEventValues,
    is_outdoor: false
  });
  const [suppliers, setSuppliers] = useState([]);

  // Load suppliers when component mounts
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const token = tokenManager.getToken();
        const response = await fetch('/api/suppliers', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const suppliersData = await response.json();
          setSuppliers(suppliersData);
        }
      } catch (error) {
        console.error('Error loading suppliers:', error);
      }
    };
    loadSuppliers();
  }, []);

  useEffect(() => {
    if (event) {
      // Parse additional_dates from JSON string if present
      let additionalDates = [];
      if (event.additional_dates) {
        try {
          additionalDates = JSON.parse(event.additional_dates).map(d => new Date(d));
        } catch (e) {
          console.error('Error parsing additional_dates:', e);
        }
      }

      setFormData({
        name: event.name || '',
        date: event.date ? new Date(event.date) : new Date(),
        additional_dates: additionalDates,
        location: event.location || '',
        venue_address: event.venue_address || '',
        hotel_name: event.hotel_name || '',
        hotel_address: event.hotel_address || '',
        get_in_time: event.get_in_time || '',
        soundcheck_time: event.soundcheck_time || '',
        logistics_required: event.logistics_required !== false,
        venue_width: event.venue_width || '',
        venue_height: event.venue_height || '',
        venue_depth: event.venue_depth || '',
        description: event.description || '',
        supplier: event.supplier || '',
        supplier_id: event.supplier_id || null,
        responsible_technician: event.responsible_technician || '',
        is_outdoor: event.is_outdoor || false
      });
    } else {
      // Load prefill settings for new events
      try {
        const savedSettings = localStorage.getItem('eventPrefillSettings');
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          setFormData(prev => ({
            ...prev,
            supplier: settings.defaultSupplier || '',
            supplier_id: settings.defaultSupplierId || null,
            responsible_technician: settings.defaultResponsibleTech || ''
          }));
        }
      } catch (error) {
        console.error('Error loading prefill settings:', error);
      }
    }
  }, [event]);

  useEffect(() => {
    if (event || suppliers.length === 0) return;
    try {
      const saved = localStorage.getItem('eventPrefillSettings');
      const settings = { ...defaultEventValues, ...(saved ? JSON.parse(saved) : {}) };
      const supplier = suppliers.find((item) => item.name === (settings.defaultSupplier || defaultEventValues.supplier))
        || suppliers.find((item) => Number(item.id) === Number(settings.defaultSupplierId))
        || suppliers.find((item) => item.name === defaultEventValues.supplier);
      setFormData(prev => ({
        ...prev,
        supplier: supplier?.name || defaultEventValues.supplier,
        supplier_id: supplier ? Number(supplier.id) : Number(settings.defaultSupplierId || defaultEventValues.supplier_id),
        responsible_technician: settings.defaultResponsibleTech || defaultEventValues.responsible_technician
      }));
    } catch (error) {
      console.error('Error applying event defaults:', error);
    }
  }, [event, suppliers]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Event name is required');
      return;
    }

    // Convert additional_dates array to JSON string
    const additionalDatesJson = formData.additional_dates.length > 0
      ? JSON.stringify(formData.additional_dates.map(serializeLocalDate))
      : null;

    // Convert date to ISO string for API and handle empty numeric fields
    const eventData = {
      ...formData,
      date: serializeLocalDate(formData.date),
      additional_dates: additionalDatesJson,
      venue_width: formData.venue_width === '' ? null : parseFloat(formData.venue_width) || null,
      venue_height: formData.venue_height === '' ? null : parseFloat(formData.venue_height) || null,
      venue_depth: formData.venue_depth === '' ? null : parseFloat(formData.venue_depth) || null
    };

    onSave(eventData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>{event ? 'Edit Event' : 'Create Event'}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Event Name *</label>
            <input
              type="text"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter event name"
              required
            />
          </div>

          <div className="form-group">
            <label>Primary Date *</label>
            <DatePicker
              selected={formData.date}
              onChange={(date) => setFormData(prev => ({ ...prev, date }))}
              className="form-control"
              dateFormat="dd/MM/yyyy"
              calendarStartDay={1}
              required
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Additional Dates (for multi-day events)</span>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '4px 8px', fontSize: '11px' }}
                onClick={() => {
                  // Add a new date, defaulting to the next day after the last date
                  const allDates = [formData.date, ...formData.additional_dates];
                  const lastDate = new Date(Math.max(...allDates.map(d => d.getTime())));
                  const nextDate = new Date(lastDate);
                  nextDate.setDate(nextDate.getDate() + 1);
                  setFormData(prev => ({
                    ...prev,
                    additional_dates: [...prev.additional_dates, nextDate]
                  }));
                }}
              >
                <Plus size={12} /> Add Date
              </button>
            </label>
            {formData.additional_dates.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {formData.additional_dates.map((additionalDate, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <DatePicker
                      selected={additionalDate}
                      onChange={(date) => {
                        const newDates = [...formData.additional_dates];
                        newDates[index] = date;
                        setFormData(prev => ({ ...prev, additional_dates: newDates }));
                      }}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      calendarStartDay={1}
                    />
                    <button
                      type="button"
                      className="btn btn-danger"
                      style={{ padding: '4px 8px', fontSize: '11px', flexShrink: 0 }}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          additional_dates: prev.additional_dates.filter((_, i) => i !== index)
                        }));
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {formData.additional_dates.length === 0 && (
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>
                No additional dates. Click "Add Date" for multi-day events.
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              name="location"
              className="form-control"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter event location"
            />
          </div>

          <div className="form-group">
            <label>Venue Address <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— used for travel, not shown in the event list</span></label>
            <input type="text" name="venue_address" className="form-control" value={formData.venue_address} onChange={handleChange} placeholder="Street address, city" />
          </div>

          <div className="form-group">
            <label>Get-in & soundcheck</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <input type="text" name="get_in_time" className="form-control" value={formData.get_in_time} onChange={handleChange} placeholder="Get-in, e.g. 14:00" />
              <input type="text" name="soundcheck_time" className="form-control" value={formData.soundcheck_time} onChange={handleChange} placeholder="Soundcheck, e.g. 16:00" />
            </div>
          </div>

          <div className="form-group">
            <label>Hotel / overnight stop</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
              <input type="text" name="hotel_name" className="form-control" value={formData.hotel_name} onChange={handleChange} placeholder="Hotel name" />
              <input type="text" name="hotel_address" className="form-control" value={formData.hotel_address} onChange={handleChange} placeholder="Hotel address" />
            </div>
          </div>


          <div className="form-group">
            <label>Venue Dimensions (meters) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>- Optional</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', marginBottom: '4px' }}>Width</label>
                <input
                  type="number"
                  name="venue_width"
                  className="form-control"
                  value={formData.venue_width}
                  onChange={handleChange}
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', marginBottom: '4px' }}>Depth</label>
                <input
                  type="number"
                  name="venue_depth"
                  className="form-control"
                  value={formData.venue_depth}
                  onChange={handleChange}
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', marginBottom: '4px' }}>Height</label>
                <input
                  type="number"
                  name="venue_height"
                  className="form-control"
                  value={formData.venue_height}
                  onChange={handleChange}
                  min="0"
                  step="0.1"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Quick Notes</label>
            <textarea
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add notes for this event"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Leverandør (Supplier)</label>
            <select
              name="supplier_id"
              className="form-control"
              value={formData.supplier_id ? String(formData.supplier_id) : ''}
              onChange={(e) => {
                const selectedSupplierId = e.target.value ? Number(e.target.value) : null;
                const selectedSupplier = suppliers.find(s => Number(s.id) === selectedSupplierId);
                setFormData(prev => ({
                  ...prev,
                  supplier_id: selectedSupplierId,
                  supplier: selectedSupplier ? selectedSupplier.name : ''
                }));
              }}
            >
              <option value="">Select a supplier...</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Ansvarlig Pyrotekniker (Responsible Technician)</label>
            <input
              type="text"
              name="responsible_technician"
              className="form-control"
              value={formData.responsible_technician}
              onChange={handleChange}
              placeholder="Enter responsible technician"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_outdoor"
                checked={formData.is_outdoor}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>Outdoor Event (affects wind risk considerations)</span>
            </label>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input type="checkbox" name="logistics_required" checked={formData.logistics_required} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
              <span>Local logistics required for this event</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn">
              {event ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;
